/**
 * @ken/terminology — vertical-aware label resolution.
 *
 * Per ADR-035 + KEN_LAYER_7 §6.6.
 *
 * Verticals use different words for the same canonical entity. A dental
 * clinic says "patient." A food distributor says "customer." A salon says
 * "client." This package provides the resolution layer:
 *
 *   - `<TerminologyProvider>` — host wraps the app. Provides the resolved
 *     terminology map from the manifest_resolver service.
 *   - `useTerm(termKey)` — returns the resolved string for the current
 *     tenant.
 *   - `<Term termKey="..." />` — React component for inline use.
 *
 * Resolution follows the four-layer chain in ADR-034: module default →
 * vertical default → tenant override → user view preference. Modules
 * never hardcode "Customer" / "Patient" / etc. — ESLint rule
 * `ken/no-hardcoded-entity-labels` enforces this at build time.
 *
 * The term map and the missing-key rule live in [`labels.ts`](./labels.ts),
 * which has no JSX so both can be tested as plain functions. UI-14: a key
 * nobody registered throws in development and test, and is humanised in
 * production — it is never printed raw.
 *
 * Concierge integration (KEN_LAYER_7 §7.5): L4 receives the resolved
 * terminology map at conversation start via system prompt injection.
 */

import { createContext, type ReactElement, type ReactNode, useContext, useMemo } from "react";

import {
  DEFAULT_TERMS,
  MissingTermError,
  humaniseTermKey,
  isStrictTermMode,
  resolveTerm,
  type TermKey,
  type TermMap,
} from "./labels";

export {
  DEFAULT_TERMS,
  MissingTermError,
  humaniseTermKey,
  isStrictTermMode,
  resolveTerm,
  type TermKey,
  type TermMap,
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface TerminologyContext {
  terms: TermMap;
}

const Ctx = createContext<TerminologyContext>({ terms: DEFAULT_TERMS });

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export interface TerminologyProviderProps {
  /** Resolved terminology map from /api/shell/manifest.terminology. */
  terms: TermMap;
  children: ReactNode;
}

export function TerminologyProvider({ terms, children }: TerminologyProviderProps): ReactElement {
  const value = useMemo<TerminologyContext>(
    () => ({ terms: { ...DEFAULT_TERMS, ...terms } }),
    [terms],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the resolved string for `termKey`: the active vertical's word when
 * it declares one, otherwise the shared generic term.
 *
 * An unregistered key is NOT returned as itself. In development and test it
 * throws {@link MissingTermError}; in production it is humanised. See
 * [`labels.ts`](./labels.ts) for why.
 */
export function useTerm(termKey: TermKey): string {
  const { terms } = useContext(Ctx);
  return resolveTerm(terms, termKey);
}

/**
 * The whole resolved word map (shared defaults overlaid with the company's
 * words). Unlike {@link useTerm} it never throws: callers that look up keys
 * that are optional by design (the menu's `nav.<module>.<slug>` words, which
 * exist only when a template or company sets them, SS-B7.20) read the map and
 * fall back to their own text.
 */
export function useTermMap(): TermMap {
  return useContext(Ctx).terms;
}

// ---------------------------------------------------------------------------
// Inline component
// ---------------------------------------------------------------------------

export interface TermProps {
  termKey: TermKey;
  /** Optional transform — e.g. capitalise first letter, lowercase. */
  transform?: "upper" | "lower" | "title" | "asis";
}

export function Term({ termKey, transform = "asis" }: TermProps): ReactElement {
  const value = useTerm(termKey);
  let display = value;
  if (transform === "upper") display = value.toUpperCase();
  else if (transform === "lower") display = value.toLowerCase();
  else if (transform === "title") {
    display = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
  return <>{display}</>;
}
